import React, { useRef, ReactElement, useEffect } from 'react';
import { VariableSizeList as VariableWindowList } from 'react-window';
import styled from 'styled-components';
import { CustomOption } from './types';

export type SelectMode = 'basic' | 'user' | 'tag';

const flattenGroupedChildrens = (children: ReactElement[]) => {
  return children
    .map((child) => [
      {
        props: {
          data: {
            type: 'groupHeading',
            label: child.props?.data?.label,
          },
        },
      },
      ...(child.props?.children ?? []),
    ])
    .reduce(
      (accumulator, currentValue) => accumulator.concat(currentValue),
      [],
    );
};

export const optionHeight = {
  groupHeading: 36,
  user: 40,
  userDescription: 56,
  tag: 46,
  tagDescription: 56,
  basic: 36,
  allOptions: 56,
};

const computeMenuItemHeight = (
  option: CustomOption & { type: 'groupHeading' | string },
  type: SelectMode,
) => {
  if (!option.label) {
    return 0;
  }
  if (option.type === 'groupHeading') {
    return optionHeight.groupHeading;
  }
  if (option.extra?.type === 'all-options') {
    return optionHeight.allOptions;
  }

  if (type === 'user') {
    return option.description
      ? optionHeight.userDescription
      : optionHeight.user;
  }
  if (type === 'tag') {
    return option.description ? optionHeight.tagDescription : optionHeight.tag;
  }

  return optionHeight.basic;
};

const GroupHeading = styled.div`
  color: var(--gray-60);
  cursor: default;
  display: block;
  font-size: var(--font-size-sm);
  font-weight: 500;
  margin-bottom: var(--spacing-02);
  padding: var(--spacing-04) var(--spacing-04) var(--spacing-02);
  text-transform: uppercase;
`;

const RowRenderer = ({ index, style, data }) => {
  return (
    <div style={style}>
      {data[index]?.props?.data?.type === 'groupHeading' ? (
        <GroupHeading>{data[index]?.props?.data?.label}</GroupHeading>
      ) : (
        data[index]
      )}
    </div>
  );
};

const VariableListRowRenderer = (props) => {
  const {
    children,
    selectProps: { maxMenuHeight, grouped, type = 'basic', isMulti },
  } = props;

  const transformedChild = grouped
    ? flattenGroupedChildrens(children)
    : children;
  const itemCount = transformedChild.length;

  const listRef = useRef<VariableWindowList>(null);

  // Reset cache to make react window recalculate styles once the list of childrens are modified.
  const resetWindowedListCachePosition = () => {
    transformedChild.forEach((child, index) => {
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    });
  };

  useEffect(() => {
    resetWindowedListCachePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedChild.length]);

  return (
    <VariableWindowList
      height={maxMenuHeight}
      itemCount={isMulti ? itemCount + 1 : itemCount}
      itemData={transformedChild}
      ref={listRef}
      itemSize={(index: number) => {
        return computeMenuItemHeight(
          transformedChild[index]?.props?.data || [],
          type,
        );
      }}
      width="100%"
    >
      {RowRenderer}
    </VariableWindowList>
  );
};

export const VirtualizedMenuList = (props): React.JSX.Element => {
  const { children } = props;

  // For handling no options
  if (children && !Array.isArray(children)) {
    return children;
  }

  return <VariableListRowRenderer {...props} />;
};
